import type { PlanMetadata } from '../sync-engine/schema/entities';

/**
 * A user's subscription tier. Canonical home for the type so the pure credit
 * logic in `@flashka/shared` doesn't have to import from the Hono server
 * (which pulls in auth-worker and other server-only deps).
 *
 * - `basic`  — free tier: per-feature daily slots via `standardSlotPolicy`
 *   (1 quiz / 1 card generation / 1 transcription / 1 oral exam / 1 case
 *   study per day, 10 Ka chat messages) — see `FREE_*_PER_DAY` in kinds.ts
 * - `beast`  — paid tier: the same slot wallets with monthly caps
 *   (`BEAST_*_PER_MONTH`, e.g. 30 quizzes and 300 chat messages per month)
 * - `revolutBeast` — Revolut partner grant. Mirrors `beast` today (same
 *   policies everywhere, via {@link isBeastTier}) but is a distinct plan so
 *   it can stay stable when `beast` evolves.
 * - `wizard` / `veteran` / `master` — unlimited tiers
 */
export const SUBSCRIPTION_STATUSES = [
  'wizard',
  'veteran',
  'master',
  'basic',
  'beast',
  'revolutBeast',
] as const;

export type UserSubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/** Client entitlement tier: the raw status minus `revolutBeast`. */
export type PlanTier = Exclude<UserSubscriptionStatus, 'revolutBeast'>;

/**
 * THE single collapse point: `revolutBeast` → 'beast'.
 * `never` guard: a new status fails compilation here.
 */
export function toPlanTier(status: UserSubscriptionStatus): PlanTier {
  switch (status) {
    case 'revolutBeast':
      return 'beast';
    case 'wizard':
    case 'veteran':
    case 'master':
    case 'basic':
    case 'beast':
      return status;
    default: {
      const unhandled: never = status;
      throw new Error(`Unhandled subscription status: ${unhandled as string}`);
    }
  }
}

/**
 * Beast-grade tiers: metered monthly plans sharing beast's credit policies.
 * Use this (never `status === 'beast'`) wherever a policy applies to the
 * beast family, so `revolutBeast` keeps mirroring `beast` in one place.
 */
export function isBeastTier(
  status: UserSubscriptionStatus,
): status is 'beast' | 'revolutBeast' {
  return status === 'beast' || status === 'revolutBeast';
}

/**
 * Tiers the payment webhooks write to `status` for an ACTIVE paid subscription.
 * By exclusion, so a new tier added to the union is paid unless listed free
 * here.
 */
export function isPaidSubscription(status: UserSubscriptionStatus): boolean {
  return status !== 'basic' && status !== 'revolutBeast';
}

/** The subset of auth-worker plan columns that determines the tier. */
export interface SubStatusMetadata {
  status?: unknown;
  /** Partner grant (e.g. Revolut) — `'revolutBeast'`, no expiration; removed
   *  by the revocation CLI. Only applies while the user's own tier is
   *  `basic`, so the grant coexists with (and survives) a paid
   *  subscription. */
  partnerStatus?: unknown;
}

/**
 * Resolve a user's effective tier from their plan metadata, taking partner
 * subscriptions into account.
 *
 * A live referral grant used to win ahead of the partner lift. The referral
 * programme is retired and its last grant lapsed on 2025-11-27, so the branch
 * is gone. `nowISO` is kept in the signature because every call site passes it
 * and a time-dependent grant is the obvious next thing to arrive here.
 */
export function resolveSubStatusFromMetadata(
  publicMetadata: SubStatusMetadata,
  _nowISO: string,
): UserSubscriptionStatus {
  const status = (publicMetadata.status ?? 'basic') as UserSubscriptionStatus;
  if (publicMetadata.partnerStatus === 'revolutBeast' && status === 'basic') {
    return 'revolutBeast';
  }
  return status;
}

/**
 * Filter plan metadata down to the keys mirrored by the `plan` entity — THE
 * single definition of the subset; bump `PLAN_MIRROR_VERSION`
 * (sync-engine/schema/entities.ts) whenever it changes. Non-string values are
 * dropped (matches `planSchema`).
 *
 * Dropping the two referral keys did NOT bump that version, deliberately. The
 * version exists to invalidate a mirror MISSING a newly-required key; here the
 * stored docs carry two keys too many, `planSchema` is `.passthrough()` so they
 * still parse, and the resolver no longer reads them. A bump would instead make
 * every stored mirror read as "tier unknown" and re-pull the auth worker for
 * every user, for no change in the answer. The next authoritative pull
 * rewrites the doc without them on its own.
 */
export function pickPlanMetadata(publicMetadata: SubStatusMetadata): PlanMetadata {
  const picked: PlanMetadata = {};
  if (typeof publicMetadata.status === 'string') picked.status = publicMetadata.status;
  if (typeof publicMetadata.partnerStatus === 'string') {
    picked.partnerStatus = publicMetadata.partnerStatus;
  }
  return picked;
}
